import { useMemo, useState, type ReactNode } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { MetricsTrend } from "../types";
import { fieldLabel } from "./codeTableToolbar";
import { CORPUS_SEGMENTS, OUTCOME_SEGMENTS } from "./segments";
import {
  agreementPoints,
  hasTrend,
  outcomePoints,
  selectBuckets,
  throughputPoints,
  trendFields,
  type AgreementPoint,
  type Granularity,
  type OutcomePoint,
  type RateDelta,
  type ThroughputPoint,
  type TrendField,
  type TrendWindow,
} from "./metricsTrend";

const TEAL = "var(--ch-teal-500)";
const MUTED = "var(--ch-gray-300)";
const CORAL = "var(--ch-coral-500)";
const FLAT = "var(--ch-gray-500)";
const pct = (v: number) => `${Math.round(v * 100)}%`;

const seg = (on: boolean) =>
  "rounded-coronis border px-2.5 py-1 text-[12px] font-medium transition-colors " +
  (on ? "border-transparent bg-foreground text-background" : "border-border bg-card text-muted-foreground hover:bg-muted");

/** Delta against the prior measured week, in points. Signed and coloured —
 *  the same "direction × whether up is good" rule as a stat tile's delta
 *  (teal = up/good, coral = down, gray = flat) — and absent (not "0") when
 *  fewer than two weeks are measured. */
export function DeltaLine({ delta }: { delta: RateDelta | null }) {
  if (!delta) return null;
  const up = delta.points > 0, flat = delta.points === 0;
  return (
    <span className="text-[11.5px] tabular-nums" style={{ color: flat ? FLAT : up ? TEAL : CORAL }}>
      {flat ? "±0" : `${up ? "+" : ""}${delta.points}`} pts vs prior week
    </span>
  );
}

/** One segment of a stacked bar: the shared identity the TABLES already draw
 *  (key, label, colour — never redefined here), plus the two fields on a point
 *  that carry its share (the bar's height) and its count (the tooltip and the
 *  table view). */
interface Stack<T> {
  key: string;
  label: string;
  color: string;
  shareKey: keyof T & string;
  countKey: keyof T & string;
}

/** Segment key → the point fields that carry it. Keyed rather than positional
 *  so reordering `OUTCOME_SEGMENTS`/`CORPUS_SEGMENTS` (which reorders the bar
 *  AND its legend together, by design) can never silently re-pair a segment
 *  with another segment's numbers. `keyof` makes every entry compiler-checked
 *  against the point interface. */
const OUTCOME_ACCESS: Record<string, { shareKey: keyof OutcomePoint & string; countKey: keyof OutcomePoint & string }> = {
  both: { shareKey: "bothShare", countKey: "both" },
  agent: { shareKey: "agentShare", countKey: "agent" },
  coder: { shareKey: "coderShare", countKey: "coder" },
  neither: { shareKey: "neitherShare", countKey: "neither" },
};
const CORPUS_ACCESS: Record<string, { shareKey: keyof AgreementPoint & string; countKey: keyof AgreementPoint & string }> = {
  agree: { shareKey: "agreeShare", countKey: "agree" },
  differ: { shareKey: "differShare", countKey: "differ" },
  one_sided: { shareKey: "oneSidedShare", countKey: "oneSided" },
  not_comparable: { shareKey: "notComparableShare", countKey: "notComparable" },
};

const OUTCOME_STACK: Stack<OutcomePoint>[] = OUTCOME_SEGMENTS.map((s) => ({
  key: s.key, label: s.label, color: s.color, ...OUTCOME_ACCESS[s.key],
}));
const CORPUS_STACK: Stack<AgreementPoint>[] = CORPUS_SEGMENTS.map((s) => ({
  key: s.key, label: s.label, color: s.color, ...CORPUS_ACCESS[s.key],
}));

const legendOf = <T,>(stack: Stack<T>[], volume: [string, string]): [string, string][] => [
  ...stack.map((s): [string, string] => [s.color, s.label]),
  volume,
];

/** A tooltip that spells the whole stack out — every segment's label, count and
 *  share, in bar order — rather than the one segment the cursor happens to be
 *  over. A stacked bar is read as a mix, so the mix is what the hover has to
 *  say; `footer` carries the denominator (and, for the verdict stack, the
 *  reason an empty slot is empty) beneath it.
 *
 *  `denom` returning 0 is the signal for "no bar here": the segment rows are
 *  suppressed entirely rather than printed as four 0%s, which is the same rule
 *  the null shares enforce on the bar itself. */
function stackTooltip<T extends { label: string }>(stack: Stack<T>[], denom: (p: T) => number, footer: (p: T) => ReactNode) {
  return function StackTooltip({ active, payload }: { active?: boolean; payload?: { payload: T }[] }) {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    const n = denom(p);
    return (
      <div className="rounded-coronis border border-border bg-card px-3 py-2 text-[12px] text-foreground shadow">
        <div className="font-medium">{p.label}</div>
        {n > 0 && (
          <div className="mt-1 flex flex-col gap-0.5">
            {stack.map((s) => {
              const c = Number(p[s.countKey]);
              return (
                <div key={s.key} className="flex items-center gap-2">
                  <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: s.color }} aria-hidden />
                  <span className="text-muted-foreground">{s.label}</span>
                  <span className="ml-auto tabular-nums">{c} · {pct(c / n)}</span>
                </div>
              );
            })}
          </div>
        )}
        <div className="mt-1 text-muted-foreground">{footer(p)}</div>
      </div>
    );
  };
}

const OutcomeTooltip = stackTooltip<OutcomePoint>(
  OUTCOME_STACK,
  (p) => (p.measured ? p.judged : 0),
  (p) =>
    p.measured && p.judged > 0
      ? `${p.judged} judgements · ${p.reviewed} reviewed`
      : `${p.reviewed} reviewed — below the floor, not measured`,
);

const CorpusTooltip = stackTooltip<AgreementPoint>(
  CORPUS_STACK,
  (p) => p.total,
  (p) => (p.total > 0 ? `${p.scored} scored · ${p.casesReceived} cases received` : "Nothing comparable this period"),
);

const axis = { tick: { fontSize: 11, fill: "var(--ch-gray-500)" }, axisLine: false, tickLine: false } as const;

/** A share axis is always the whole 0-100%, on quarter ticks. A stacked
 *  100%-height bar has no "fit the axis to the data" question — the stack IS
 *  the data, and a clipped share axis would draw a mix that does not add up. */
const SHARE_TICKS = [0, 0.25, 0.5, 0.75, 1];

/** The trend twin of the tables' `OutcomeBar`/`AgreementBar`: one 100%-height
 *  stacked bar per bucket, same segments, same order, same colours — so a row
 *  in the table above and a bar in the chart below are read the same way.
 *
 *  Two single-axis charts stacked, never one plot with two y-scales: the share
 *  mix above, and a volume bar sharing its x categories below. A dual-axis
 *  combination invents a correlation from an arbitrary scale pairing — this
 *  keeps the reader to one axis per plot and lets the volume bar supply "how
 *  much this mix should be trusted" as its own, honestly-scaled chart. It also
 *  explains the gaps: a bucket below the blind-review floor has all-null
 *  shares and draws no bar at all, and the volume pane underneath is what says
 *  the period was not empty, merely not measured. */
function StackedShare<T extends { start: string; label: string }>({
  data,
  stack,
  volumeKey,
  Tip,
}: {
  data: T[];
  stack: Stack<T>[];
  volumeKey: keyof T & string;
  Tip: React.ComponentType<{ active?: boolean; payload?: { payload: T }[] }>;
}) {
  // Recharts' typed `dataKey` narrows against the exact object type inferred
  // from the `data` prop, which defeats a generically-typed wrapper like this
  // one (it can't express "some key of T whose value recharts will accept").
  // We already constrain `stack`/`volumeKey` to `keyof T` above, so the real
  // safety is there; this local widening is just to hand recharts a shape its
  // own generic can index by any string.
  const rows = data as unknown as Record<string, unknown>[];
  return (
    <div className="flex flex-col gap-0.5">
      <ResponsiveContainer width="100%" height={168}>
        {/* left: 0 and this chart's YAxis width MUST match the volume
            BarChart's below — same width, same margin.left — so the bars stay
            aligned under their stacks on the shared x categories. That
            alignment is the entire reason these are two stacked single-axis
            charts instead of one dual-axis plot. */}
        <BarChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke="var(--ch-gray-200)" />
          <XAxis dataKey="label" {...axis} tick={false} interval="preserveStartEnd" />
          <YAxis domain={[0, 1]} ticks={SHARE_TICKS} tickFormatter={pct} {...axis} width={44} />
          <Tooltip content={<Tip />} cursor={{ fill: "var(--ch-gray-100)" }} />
          {/* Declaration order is stack order, bottom-up — the same order the
              legend and the tables' bars read in. */}
          {stack.map((s) => (
            <Bar<Record<string, unknown>>
              key={s.key}
              dataKey={s.shareKey}
              stackId="o"
              fill={s.color}
              maxBarSize={28}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
      {/* Same width={44}/margin.left:0 as the share chart above — see the
          comment there. */}
      <ResponsiveContainer width="100%" height={72}>
        <BarChart data={rows} margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
          <XAxis dataKey="label" {...axis} interval="preserveStartEnd" />
          <YAxis domain={[0, "auto"]} allowDecimals={false} tickCount={2} {...axis} width={44} />
          <Bar<Record<string, unknown>> dataKey={volumeKey} fill={MUTED} maxBarSize={20} radius={[2, 2, 0, 0]} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function TableView<T extends object>({
  rows,
  columns,
}: {
  rows: T[];
  columns: { key: keyof T; label: string; format?: (v: unknown) => string }[];
}) {
  if (rows.length === 0) return null;
  return (
    <details className="mt-1">
      <summary className="cursor-pointer select-none text-[11px] font-medium text-muted-foreground hover:text-foreground">
        View as table
      </summary>
      <div className="mt-2 max-h-56 overflow-auto rounded-coronis border border-border">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-left text-[10.5px] font-medium uppercase tracking-[0.04em] text-muted-foreground">
              {columns.map((c) => (
                <th key={String(c.key)} className="px-3 py-1.5 font-medium">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-border last:border-0">
                {columns.map((c) => (
                  <td key={String(c.key)} className="px-3 py-1.5 tabular-nums text-foreground">
                    {c.format ? c.format(r[c.key]) : String(r[c.key] ?? "")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

function Panel({ title, legend, children }: { title: string; legend: [string, string][]; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2 rounded-coronis border border-border bg-card px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <span className="text-[12px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{title}</span>
        {/* A single series names itself in the title above; a legend box with
            one swatch would only restate it. */}
        {legend.length > 1 && (
          <span className="flex flex-wrap items-center gap-3">
            {legend.map(([color, label]) => (
              <span key={label} className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
                <span className="size-2 rounded-full" style={{ backgroundColor: color }} aria-hidden /> {label}
              </span>
            ))}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

export function TrendSection({ trend }: { trend: MetricsTrend }) {
  const [g, setG] = useState<Granularity>("week");
  const [w, setW] = useState<TrendWindow>("recent");
  const [field, setField] = useState<TrendField>("all");
  const fields = useMemo(() => trendFields(trend), [trend]);
  // A field that disappears between two loads must not strand the filter on
  // it — the charts would read as an empty study rather than as a control
  // pointed at something the server no longer sends.
  const f: TrendField = field !== "all" && !fields.includes(field) ? "all" : field;
  const buckets = useMemo(() => selectBuckets(g === "day" ? trend.daily : trend.weekly, g, w), [trend, g, w]);
  const outcome = useMemo(() => outcomePoints(buckets, g, f), [buckets, g, f]);
  const corpus = useMemo(() => agreementPoints(buckets, g, f), [buckets, g, f]);
  const reviews = useMemo(() => throughputPoints(selectBuckets(g === "day" ? trend.reviewsDaily : trend.reviewsWeekly, g, w), g), [trend, g, w]);
  const suffix = f === "all" ? "" : ` · ${fieldLabel(f)}`;
  if (!hasTrend(trend)) return null;

  return (
    <div className="flex flex-col gap-3" data-testid="trend-section">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-[13px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Trends</h2>
        <span className="flex flex-wrap items-center gap-1.5">
          <button type="button" aria-pressed={g === "day"} className={seg(g === "day")} onClick={() => setG("day")}>
            Day
          </button>
          <button type="button" aria-pressed={g === "week"} className={seg(g === "week")} onClick={() => setG("week")}>
            Week
          </button>
          <span className="mx-1 h-4 border-l border-border" />
          <button type="button" aria-pressed={w === "recent"} className={seg(w === "recent")} onClick={() => setW("recent")}>
            {g === "day" ? "Last 30 days" : "Last 12 weeks"}
          </button>
          <button type="button" aria-pressed={w === "all"} className={seg(w === "all")} onClick={() => setW("all")}>
            All
          </button>
        </span>
      </div>
      {/* Filters in one row above the charts, same segmented styling as the
          granularity toggles. Rendered only when the server actually sent
          per-field slices — an older server sends none, and a row offering
          "All" alone would be a control that does nothing. */}
      {fields.length > 0 && (
        <span className="flex flex-wrap items-center gap-1.5" data-testid="trend-field" data-field={f}>
          <button type="button" aria-pressed={f === "all"} className={seg(f === "all")} onClick={() => setField("all")}>
            All
          </button>
          {fields.map((key) => (
            <button key={key} type="button" aria-pressed={f === key} className={seg(f === key)} onClick={() => setField(key)}>
              {fieldLabel(key)}
            </button>
          ))}
        </span>
      )}
      <p className="text-[12.5px] leading-snug text-muted-foreground">
        By the {g} a case was <em>received</em>, so a change here is the model changing, not a reviewer catching up. Each
        bar is one {g}&rsquo;s outcome mix, read exactly like the bars in the tables below. A {g} with too few reviews
        from too few reviewers is left blank rather than drawn as 0%.
        {f !== "all" && ` Showing ${fieldLabel(f)} only; the rate cards above stay study-wide.`}
      </p>
      <div className="grid gap-3 lg:grid-cols-2">
        <Panel title={`Outcome mix by received date${suffix}`} legend={legendOf(OUTCOME_STACK, [MUTED, "Reviewed (n)"])}>
          <StackedShare<OutcomePoint> data={outcome} stack={OUTCOME_STACK} volumeKey="reviewed" Tip={OutcomeTooltip} />
          <TableView
            rows={outcome}
            columns={[
              { key: "label", label: "Period" },
              ...OUTCOME_STACK.map((s) => ({ key: s.countKey, label: s.label })),
              { key: "judged", label: "Judged" },
              { key: "reviewed", label: "Reviewed" },
            ]}
          />
        </Panel>
        <Panel title={`Corpus by received date${suffix}`} legend={legendOf(CORPUS_STACK, [MUTED, "Scored (n)"])}>
          <StackedShare<AgreementPoint> data={corpus} stack={CORPUS_STACK} volumeKey="scored" Tip={CorpusTooltip} />
          <TableView
            rows={corpus}
            columns={[
              { key: "label", label: "Period" },
              ...CORPUS_STACK.map((s) => ({ key: s.countKey, label: s.label })),
              { key: "scored", label: "Scored" },
            ]}
          />
        </Panel>
        <Panel title="Reviews submitted" legend={[[TEAL, "Reviews"]]}>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={reviews} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="var(--ch-gray-200)" />
              <XAxis dataKey="label" {...axis} interval="preserveStartEnd" />
              <YAxis allowDecimals={false} {...axis} width={44} />
              <Tooltip />
              <Bar dataKey="reviews" fill={TEAL} maxBarSize={24} radius={[4, 4, 0, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
          <TableView<ThroughputPoint>
            rows={reviews}
            columns={[
              { key: "label", label: "Period" },
              { key: "reviews", label: "Reviews" },
            ]}
          />
        </Panel>
      </div>
    </div>
  );
}
